import { FileButton } from '../components/FileButton';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DatasetExport, Model } from '@visionqc/shared';
import { api } from '../api/client';
import { useProducts } from '../api/useProducts';
import { useIsAdmin } from '../api/auth';
import { fmtTime } from '../lib/format';

/**
 * Training page — the bridge from annotations to a running model.
 *
 * 1. Export the annotated images as a training dataset (MVTec layout in MinIO).
 * 2. Train elsewhere (see services/training for the Anomalib PatchCore trainer).
 * 3. Upload the resulting ONNX here and activate it for the product; inference
 *    picks it up on the next inspection, and deactivating rolls straight back.
 */
export function TrainingView() {
  const isAdmin = useIsAdmin();
  const { products } = useProducts();
  const [productId, setProductId] = useState<number | undefined>();
  const [models, setModels] = useState<Model[]>([]);
  const [exports, setExports] = useState<DatasetExport[]>([]);
  const [busy, setBusy] = useState(false);
  const [bootstrap, setBootstrap] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (productId === undefined && products.length > 0) setProductId(products[0]!.id);
  }, [products, productId]);

  const load = useCallback(async () => {
    if (!productId) return;
    const [m, e] = await Promise.all([
      api.listModels(productId).catch(() => []),
      api.listExports(productId).catch(() => []),
    ]);
    setModels(m);
    setExports(e);
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<unknown>, msg?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (msg) setNotice(msg);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const activeModel = models.find((m) => m.active) ?? null;

  return (
    <div>
      <h1 className="view-title">Training</h1>
      {!isAdmin && (
        <div className="panel muted" style={{ marginBottom: 16 }}>
          You’re signed in as an operator — exporting datasets and activating models requires ADMIN.
        </div>
      )}

      <div className="panel" style={{ marginBottom: 20 }}>
        <label>Product</label>
        <select
          value={productId ?? ''}
          onChange={(e) => setProductId(e.target.value ? Number(e.target.value) : undefined)}
        >
          {products.length === 0 && <option>No products — add one in Settings</option>}
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <div className="row" style={{ marginTop: 12 }}>
          <span className="muted">Active model:</span>
          {activeModel ? (
            <span className="badge good">{activeModel.version}</span>
          ) : (
            <span className="badge unsure">stub (no trained model)</span>
          )}
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
          ⚠ Every model has its own score distribution — after activating a new one, re-tune the
          product’s <strong>threshold</strong> (and UNSURE band) in Settings. A threshold tuned for
          the previous model will misclassify.
        </p>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}
      {notice && <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>}

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', alignItems: 'start' }}>
        {/* Step 1: export */}
        <div className="panel">
          <strong>1 · Export training dataset</strong>
          <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
            Materializes every annotated image for this product into MinIO in MVTec layout
            (train/good, test/&lt;defect&gt;, ground-truth masks from your boxes). UNSURE labels are
            excluded — unresolved images shouldn’t become training truth.
          </p>
          {/* The way out of a new customer's chicken-and-egg: images but no
              labels, and no model to help produce them. */}
          <label className="row" style={{ gap: 8, fontSize: 13, cursor: 'pointer', margin: '10px 0' }}>
            <input
              type="checkbox"
              checked={bootstrap}
              onChange={(e) => setBootstrap(e.target.checked)}
            />
            Bootstrap from unlabelled images — assumes the batch is mostly good
          </label>
          <button
            className="btn primary"
            disabled={busy || !productId || !isAdmin}
            onClick={() =>
              run(
                () =>
                  api.createExport({
                    productId: productId!,
                    testSplit: 0.2,
                    bootstrapUnlabelled: bootstrap,
                  }),
                'Dataset exported.',
              )
            }
          >
            Export dataset
          </button>

          <div className="grid" style={{ gap: 8, marginTop: 16 }}>
            {exports.map((e) => (
              <div key={e.id} className="stat-tile" style={{ padding: 12 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <strong style={{ fontSize: 14 }}>{fmtTime(e.createdAt)}</strong>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {e.stats.trainGood} train · {e.stats.testGood} good · {e.stats.testBad} bad
                  </span>
                </div>
                <div className="muted" style={{ fontSize: 11, fontFamily: 'var(--mono)', marginTop: 4 }}>
                  {e.prefix}
                </div>
                {e.stats.unsureExcluded > 0 && (
                  <div className="muted" style={{ fontSize: 11 }}>
                    {e.stats.unsureExcluded} UNSURE excluded
                  </div>
                )}
              </div>
            ))}
            {exports.length === 0 && <span className="muted">No exports yet.</span>}
          </div>
        </div>

        {/* Step 2/3: models */}
        <div className="panel">
          <strong>2 · Upload &amp; activate a model</strong>
          <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
            Train on the exported dataset (see <span className="kbd">services/training</span>), then
            upload the ONNX. Activating switches inference for this product immediately;
            deactivating rolls back to the stub.
          </p>
          <ModelUpload productId={productId} disabled={busy || !isAdmin} onUploaded={load} onError={setError} />

          <div className="grid" style={{ gap: 8, marginTop: 16 }}>
            {models.map((m) => (
              <div
                key={m.id}
                className={`chip ${m.active ? 'selected' : ''}`}
                style={{ justifyContent: 'space-between', gap: 10 }}
              >
                <span>
                  <strong>{m.version}</strong>
                  <span className="muted" style={{ display: 'block', fontSize: 11 }}>
                    {(m.sizeBytes / 1024).toFixed(0)} kB · {fmtTime(m.createdAt)}
                    {Object.keys(m.metrics).length > 0 && ` · ${JSON.stringify(m.metrics)}`}
                  </span>
                </span>
                {m.active ? (
                  <button
                    className="btn sm"
                    disabled={busy || !isAdmin}
                    onClick={() => run(() => api.deactivateModel(m.id), 'Rolled back to stub.')}
                  >
                    Deactivate
                  </button>
                ) : (
                  <button
                    className="btn primary sm"
                    disabled={busy || !isAdmin}
                    onClick={() => run(() => api.activateModel(m.id), `Activated ${m.version}.`)}
                  >
                    Activate
                  </button>
                )}
              </div>
            ))}
            {models.length === 0 && <span className="muted">No models uploaded yet.</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ModelUpload({
  productId,
  disabled,
  onUploaded,
  onError,
}: {
  productId: number | undefined;
  disabled: boolean;
  onUploaded: () => void;
  onError: (msg: string) => void;
}) {
  const [version, setVersion] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || !productId || !version.trim()) return;
    setUploading(true);
    try {
      await api.uploadModel(productId, file, version.trim());
      setVersion('');
      if (fileRef.current) fileRef.current.value = '';
      onUploaded();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="row" style={{ marginTop: 12 }}>
      <input
        placeholder="Version (e.g. patchcore-2026-07-18)"
        value={version}
        onChange={(e) => setVersion(e.target.value)}
        style={{ flex: 1 }}
      />
      <FileButton inputRef={fileRef} accept=".onnx" label="Choose .onnx" style={{ maxWidth: 190 }} />
      <button className="btn primary sm" onClick={upload} disabled={disabled || uploading || !productId}>
        {uploading ? 'Uploading…' : 'Upload'}
      </button>
    </div>
  );
}
